/**
 * Classifying a preview runtime error as dev-tooling-internal (drop) vs app code (forward).
 *
 * The IDE forwards uncaught errors reported from the preview iframe to Synthase as a
 * "[Preview Error] … investigate and fix" request. But some of those errors do NOT come
 * from the user's app — they come from the dev tooling itself: Vite's HMR client
 * (`@vite/client`), the react-refresh runtime (`@react-refresh` / `react-refresh/runtime`),
 * and the Vite error overlay. Those fire TRANSIENTLY on an HMR reconnect / dev-server
 * bounce and self-resolve; they are not app bugs. Forwarding one dispatched an autonomous
 * fix turn and sent the executor on a ~10-message goose chase after a completed build (the
 * live case: `Cannot read properties of undefined (reading 'frame')` at `@vite/client:798`
 * — Vite's overlay reading `err.frame`, harmless HMR-reconnect noise).
 *
 * {@link isToolingInternalError} is the pure, unit-testable decision. It matches on EITHER
 * the error's SOURCE url (the precise signal — `@vite/client`, react-refresh, the vite
 * client bundle, browser-extension protocols) OR, when a runtime `error` event loses its
 * filename, a message signature that names the tooling UNAMBIGUOUSLY. It is deliberately
 * conservative: a generic app-code `TypeError` (e.g. a bare "Cannot read properties of
 * undefined") from the user's code is NOT tooling-internal and must still be forwarded —
 * the message match never broadens to catch all TypeErrors, only the tooling identifiers.
 *
 * @module
 */

/** The minimal shape of a preview error the classifier inspects. */
export interface PreviewErrorLike {
  /** The error message text (`e.message` from the runtime bridge). */
  message: string
  /** The source url the error originated from (`e.filename`), if the event carried one. */
  source?: string
}

/**
 * Source urls whose errors originate in the dev tooling, not the user's app:
 * Vite's HMR client (`@vite/client` / `vite/dist/client`), the react-refresh runtime, and
 * browser-extension-injected scripts. An error from any of these is never an app bug.
 */
const TOOLING_SOURCE_RE =
  /@vite\/client|vite\/dist\/client|react-refresh|chrome-extension:\/\/|moz-extension:\/\//i

/**
 * Message signatures that name the dev tooling UNAMBIGUOUSLY — the fallback for runtime
 * `error` events that lost their `filename`. Every token here is tooling-specific (the vite
 * client module path, the react-refresh runtime + its injected globals, the vite HMR
 * connection-lifecycle chatter), so it can never match a generic app-code error message.
 * It deliberately does NOT match the bare "Cannot read properties of undefined (reading
 * 'frame')" text — that is only tooling when its SOURCE is `@vite/client` (handled above);
 * matched by message alone it would swallow real app TypeErrors.
 */
const TOOLING_MESSAGE_RE =
  /@vite\/client|vite\/dist\/client|@react-refresh|react-refresh\/runtime|\$RefreshReg\$|\$RefreshSig\$|\bRefreshRuntime\b|\[vite\] (?:connecting|connected|server connection lost)|failed to connect to websocket|chrome-extension:\/\/|moz-extension:\/\//i

/**
 * Whether a preview error is dev-tooling-internal (drop it) rather than an app bug
 * (forward it to the executor). Matches on the SOURCE url first (precise), then falls back
 * to an unambiguous tooling MESSAGE signature for events that lost their filename.
 *
 * @param error - The reported preview error ({@link PreviewErrorLike}).
 * @returns `true` when the error originates in Vite/react-refresh/the vite overlay or a
 *   browser extension — i.e. it self-resolves and must NOT trigger an autonomous fix turn.
 */
export function isToolingInternalError(error: PreviewErrorLike): boolean {
  if (error.source && TOOLING_SOURCE_RE.test(error.source)) return true
  if (error.message && TOOLING_MESSAGE_RE.test(error.message)) return true
  return false
}
